// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Maps the narration's driver/impact keys onto the per-timestep series buried
 * in `raw_inputs`, for the day-ahead v1 Drivers tab.
 *
 * The narration only states peaks and totals; the charts need the underlying
 * curves, which live in three different places depending on the signal:
 *   - ambient temperature → `weather.<sensor>`
 *   - fleet-wide roll-ups → `solutions[].aggregated_summary[].kpi`
 *   - metered signals    → `solutions[].equipment[].state`
 */

import type {
  PlanEquipment,
  PlanPhase,
  PlanSignal,
  PlanSolution,
  RawInputs,
  TimeAxis,
} from '../types';
import { nextDayKey, zoneOffsetMinutes, zonedDayKey, zonedMinuteOfDay } from './siteTime';

/** `solve_strategy` values the payload uses for the two solves. */
export const BASELINE_STRATEGY = 'baseline';
export const RECOMMENDED_STRATEGY = 'optimal';

export type SeriesVariant = 'baseline' | 'recommended' | 'single' | 'live';

/**
 * How a curve joins its samples. See {@link PlanChartSeries.interpolation}.
 */
export type SeriesInterpolation = 'linear' | 'step';

export interface PlanChartSeries {
  label: string;
  values: (number | null)[];
  variant: SeriesVariant;
  interpolation?: SeriesInterpolation;
}

export interface PlanChartSpec {
  series: PlanChartSeries[];
  unit: string;
}

/** Upstream units are enum-ish (`KiloW`); charts want display symbols (`kW`). */
const UNIT_LABELS: Record<string, string> = {
  KiloW: 'kW',
  KiloWH: 'kWh',
  DEG_C: '°C',
  DEG_F: '°F',
  UNITLESS: '',
  PERCENT: '%',
};

export function formatUnit(unit: string | undefined): string {
  if (!unit) return '';
  return UNIT_LABELS[unit] ?? unit;
}

export function toFahrenheit(spec: PlanChartSpec | undefined): PlanChartSpec | undefined {
  if (!spec) return undefined;
  if (spec.unit !== '°C') return spec;
  return {
    unit: '°F',
    series: spec.series.map(s => ({
      ...s,
      values: s.values.map(v => (v === null || !Number.isFinite(v) ? v : v * 1.8 + 32)),
    })),
  };
}

export function findSolution(
  rawInputs: RawInputs | undefined,
  strategy: string
): PlanSolution | undefined {
  return rawInputs?.solutions?.find(s => s.solve_strategy === strategy);
}

export interface EnergySavings {
  /** Baseline − optimized total HVAC energy over the day, in kWh. */
  savedKwh: number;
  /** `savedKwh` as a share of the baseline total, when the baseline is known. */
  percentVsBaseline?: number;
}

export function getEnergySavings(rawInputs: RawInputs | undefined): EnergySavings | undefined {
  const savedKwh = rawInputs?.savings?.find(s => s.key === 'total_energy_kwh')?.value;
  if (typeof savedKwh !== 'number' || !Number.isFinite(savedKwh)) return undefined;

  const baselineKpi = findSolution(rawInputs, BASELINE_STRATEGY)?.aggregated_summary?.find(
    a => typeof a.kpi?.total_energy_kwh?.value === 'number'
  )?.kpi?.total_energy_kwh?.value;
  const percentVsBaseline =
    typeof baselineKpi === 'number' && baselineKpi !== 0
      ? (Math.abs(savedKwh) / baselineKpi) * 100
      : undefined;

  return { savedKwh, percentVsBaseline };
}

/** A per-timestep KPI from an aggregate roll-up, e.g. `energy_kwh`. */
function aggregateSeries(
  solution: PlanSolution | undefined,
  aggregateKey: string,
  kpiName: string
): PlanSignal | undefined {
  const kpi = solution?.aggregated_summary?.find(a => a.key === aggregateKey)?.kpi?.[kpiName];
  if (!kpi || !Array.isArray(kpi.value)) return undefined;
  return { value: kpi.value, unit: kpi.unit };
}

/** A metered signal off the first piece of equipment of a given type. */
function equipmentSeries(
  solution: PlanSolution | undefined,
  equipmentType: string,
  signal: string
): PlanSignal | undefined {
  const node = solution?.equipment?.find(e => e.type === equipmentType)?.state?.[signal];
  return Array.isArray(node?.value) ? node : undefined;
}

type SignalLookup = (_solution: PlanSolution | undefined) => PlanSignal | undefined;

export function comparisonFrom(
  baseline: { values: number[]; unit?: string } | undefined,
  recommended: { values: number[]; unit?: string } | undefined,
  baselineLabel = 'Baseline',
  recommendedLabel = 'Recommended'
): PlanChartSpec | undefined {
  if (!baseline && !recommended) return undefined;
  const series: PlanChartSeries[] = [];
  if (baseline) series.push({ label: baselineLabel, values: baseline.values, variant: 'baseline' });
  if (recommended) {
    series.push({ label: recommendedLabel, values: recommended.values, variant: 'recommended' });
  }
  return { series, unit: formatUnit(recommended?.unit ?? baseline?.unit) };
}

function comparisonSpec(rawInputs: RawInputs, lookup: SignalLookup): PlanChartSpec | undefined {
  const asSeries = (r: { value: number[]; unit?: string } | undefined) =>
    r ? { values: r.value, unit: r.unit } : undefined;
  return comparisonFrom(
    asSeries(lookup(findSolution(rawInputs, BASELINE_STRATEGY))),
    asSeries(lookup(findSolution(rawInputs, RECOMMENDED_STRATEGY)))
  );
}

function weatherSpec(
  rawInputs: RawInputs,
  sensor: string,
  label: string
): PlanChartSpec | undefined {
  const signal = rawInputs.weather?.[sensor];
  if (!Array.isArray(signal?.value)) return undefined;
  return {
    series: [{ label, values: signal.value, variant: 'single' }],
    unit: formatUnit(signal.unit),
  };
}

const SERIES_RESOLVERS: Record<string, (_rawInputs: RawInputs) => PlanChartSpec | undefined> = {
  temperature: raw => weatherSpec(raw, 'brick:Outside_Air_Temperature_Sensor', 'Temperature'),
  hvac_load: raw =>
    comparisonSpec(raw, s => aggregateSeries(s, 'hvac-energy-minimization', 'energy_kwh')),
  total_grid_load: raw =>
    comparisonSpec(raw, s => equipmentSeries(s, 'wattsch:SwitchgearType', 'wattsch:ActivePower')),
  // Peak demand is the crest of the same metered signal as total grid load.
  grid_peak_demand: raw =>
    comparisonSpec(raw, s => equipmentSeries(s, 'wattsch:SwitchgearType', 'wattsch:ActivePower')),
};

/** A resolver can match yet yield empty arrays if upstream sent `value: []`. */
function usable(spec: PlanChartSpec | undefined): PlanChartSpec | undefined {
  return spec?.series.some(s => s.values.length > 0) ? spec : undefined;
}

export function getChartSpec(
  key: string,
  rawInputs: RawInputs | undefined
): PlanChartSpec | undefined {
  if (!rawInputs) return undefined;
  return usable(SERIES_RESOLVERS[key]?.(rawInputs));
}

/** First aggregate carrying a per-timestep KPI under this exact name. */
function namedAggregateSeries(
  solution: PlanSolution | undefined,
  kpiName: string
): PlanSignal | undefined {
  for (const aggregate of solution?.aggregated_summary ?? []) {
    const kpi = aggregate.kpi?.[kpiName];
    if (kpi && Array.isArray(kpi.value)) return { value: kpi.value, unit: kpi.unit };
  }
  return undefined;
}

/** First piece of equipment carrying a state signal under this exact name. */
function namedEquipmentSeries(
  solution: PlanSolution | undefined,
  signal: string
): PlanSignal | undefined {
  for (const equipment of solution?.equipment ?? []) {
    const node = equipment.state?.[signal];
    if (Array.isArray(node?.value)) return node;
  }
  return undefined;
}

const CHILLER_TYPE = 'brick:Air_Cooled_Chiller';

function isSignal(node: unknown): node is PlanSignal {
  return !!node && Array.isArray((node as PlanSignal).value);
}

/** A leaf signal nested one level down under `part_state[part][signal]`. */
function partStateSignal(equipment: PlanEquipment, signal: string): PlanSignal | undefined {
  const parts = equipment.part_state;
  if (!parts || typeof parts !== 'object') return undefined;
  for (const partSignals of Object.values(parts)) {
    if (partSignals && typeof partSignals === 'object') {
      const node = (partSignals as Record<string, unknown>)[signal];
      if (isSignal(node)) return node;
    }
  }
  return undefined;
}

/** Per-timestep count of units of a type whose boolean `signal` is on. */
function onCountAcrossEquipment(
  solution: PlanSolution | undefined,
  equipmentType: string,
  signal: string
): PlanSignal | undefined {
  const arrays: number[][] = [];
  for (const equipment of solution?.equipment ?? []) {
    if (equipment.type !== equipmentType) continue;
    const node = equipment.state?.[signal];
    if (!Array.isArray(node?.value)) continue;
    arrays.push(node.value.map(v => (v ? 1 : 0)));
  }
  if (arrays.length === 0) return undefined;
  const length = Math.max(...arrays.map(a => a.length));
  const value = Array.from({ length }, (_, i) =>
    arrays.reduce((total, a) => total + (a[i] ?? 0), 0)
  );
  return { value, unit: 'UNITLESS' };
}

/** Per-timestep average of a `part_state` signal across units of a type. */
function averagePartStateAcrossEquipment(
  solution: PlanSolution | undefined,
  equipmentType: string,
  signal: string
): PlanSignal | undefined {
  const arrays: number[][] = [];
  let unit: string | undefined;
  for (const equipment of solution?.equipment ?? []) {
    if (equipment.type !== equipmentType) continue;
    const node = partStateSignal(equipment, signal);
    if (!node) continue;
    arrays.push(node.value.map(v => Number(v)));
    unit = node.unit ?? unit;
  }
  if (arrays.length === 0) return undefined;
  const length = Math.max(...arrays.map(a => a.length));
  const value = Array.from({ length }, (_, i) => {
    const present = arrays.map(a => a[i]).filter(v => typeof v === 'number' && Number.isFinite(v));
    if (present.length === 0) return 0;
    return present.reduce((total, v) => total + v, 0) / present.length;
  });
  return { value, unit };
}

/** Fleet-level parameters derived from per-device signals rather than a KPI. */
const DERIVED_PARAMETERS: Record<string, SignalLookup> = {
  chillers_on_count: solution =>
    onCountAcrossEquipment(solution, CHILLER_TYPE, 'brick:On_Off_Command'),
  avg_chilled_water_setpoint_c: solution =>
    averagePartStateAcrossEquipment(
      solution,
      CHILLER_TYPE,
      'brick:Supply_Chilled_Water_Temperature_Setpoint'
    ),
};

export function getParameterChartSpec(
  parameter: string,
  rawInputs: RawInputs | undefined
): PlanChartSpec | undefined {
  if (!rawInputs || !parameter) return undefined;
  const derived = DERIVED_PARAMETERS[parameter];
  return usable(
    comparisonSpec(
      rawInputs,
      solution =>
        namedAggregateSeries(solution, parameter) ??
        namedEquipmentSeries(solution, parameter) ??
        derived?.(solution)
    )
  );
}

/* ── Time axis ─────────────────────────────────────────────────────── */

/** `HH:MM` → minutes past midnight, treating `24:00` as end-of-day. */
export function parseTimeOfDay(time: string): number | undefined {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59) return undefined;
  return hours * 60 + minutes;
}

/** Fallback when the site zone is unknown: the offset the plan was optimized for beats the viewer's. */
function tzOffsetMinutes(startTime: string): number {
  const m = /([+-])(\d{2}):(\d{2})$/.exec(startTime);
  if (!m) return 0;
  const sign = m[1] === '-' ? -1 : 1;
  return sign * (Number(m[2]) * 60 + Number(m[3]));
}

/** Shift for `getUTC*()` to read the axis's wall clock; per sample, so a mid-axis DST boundary works. */
function axisOffsetMinutes(at: Date, startTime: string, timeZone?: string): number {
  return timeZone ? zoneOffsetMinutes(at, timeZone) : tzOffsetMinutes(startTime);
}

export function buildHourTicks(
  timeAxis: TimeAxis,
  everyHours = 3,
  timeZone?: string
): { index: number; label: string }[] {
  const { start_time, step_seconds, count } = timeAxis;
  if (!step_seconds || !count) return [];

  const hasTz = /(?:Z|[+-]\d{2}:\d{2})$/.test(start_time);
  const start = new Date(hasTz ? start_time : `${start_time}Z`);
  if (Number.isNaN(start.getTime())) return [];

  const stepsPerTick = Math.round((everyHours * 3600) / step_seconds);
  if (stepsPerTick <= 0) return [];

  const tickAt = (index: number) => {
    const at = new Date(start.getTime() + index * step_seconds * 1000);
    const shifted = new Date(at.getTime() + axisOffsetMinutes(at, start_time, timeZone) * 60_000);
    return { index, label: formatHourLabel(shifted.getUTCHours()) };
  };

  const ticks: { index: number; label: string }[] = [];
  for (let i = 0; i <= count; i += stepsPerTick) ticks.push(tickAt(i));
  // A DST day (92 or 100 steps) isn't a multiple of the stride; close it anyway.
  if (ticks[ticks.length - 1]?.index !== count) ticks.push(tickAt(count));
  return ticks;
}

function formatHourLabel(hour24: number): string {
  const suffix = hour24 < 12 ? 'am' : 'pm';
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}${suffix}`;
}

export function hourlyRowIndices(timeAxis: TimeAxis): number[] {
  const { step_seconds, count } = timeAxis;
  if (!step_seconds || !count) return [];
  const stepsPerHour = Math.max(1, Math.round(3600 / step_seconds));
  const indices: number[] = [];
  for (let i = 0; i < count; i += stepsPerHour) indices.push(i);
  return indices;
}

/** Minutes since midnight on the axis's clock, for intraday window checks. */
export function axisMinuteOfDay(at: Date, startTime: string, timeZone?: string): number {
  if (timeZone) return zonedMinuteOfDay(at, timeZone);
  const shifted = new Date(at.getTime() + tzOffsetMinutes(startTime) * 60_000);
  return shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
}

/** `HH:MM` for a timestep index, on the same clock as {@link buildHourTicks}. */
export function formatAxisClock(timeAxis: TimeAxis, index: number, timeZone?: string): string {
  const { start_time, step_seconds } = timeAxis;
  if (!step_seconds) return '';
  const hasTz = /(?:Z|[+-]\d{2}:\d{2})$/.test(start_time);
  const start = new Date(hasTz ? start_time : `${start_time}Z`);
  if (Number.isNaN(start.getTime())) return '';
  const at = new Date(start.getTime() + index * step_seconds * 1000);
  const shifted = new Date(at.getTime() + axisOffsetMinutes(at, start_time, timeZone) * 60_000);
  const hours = String(shifted.getUTCHours()).padStart(2, '0');
  const minutes = String(shifted.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/* ── Shaded phase bands ────────────────────────────────────────────── */

export interface PhaseBand {
  name: string;
  start: number;
  end: number;
}

export function axisStartMs(timeAxis: TimeAxis): number | undefined {
  const { start_time } = timeAxis ?? {};
  if (!start_time) return undefined;
  const hasTz = /(?:Z|[+-]\d{2}:\d{2})$/.test(start_time);
  const ms = new Date(hasTz ? start_time : `${start_time}Z`).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

/** Total span of the axis in ms, or undefined for a degenerate axis. */
function axisSpanMs(timeAxis: TimeAxis): number | undefined {
  const span = (timeAxis?.step_seconds ?? 0) * (timeAxis?.count ?? 0) * 1000;
  return span > 0 ? span : undefined;
}

export function axisFractionAt(timeAxis: TimeAxis, at: Date): number | undefined {
  const start = axisStartMs(timeAxis);
  const span = axisSpanMs(timeAxis);
  if (start === undefined || span === undefined) return undefined;
  const fraction = (at.getTime() - start) / span;
  return fraction >= 0 && fraction <= 1 ? fraction : undefined;
}

export function buildPhaseBandsOnAxis(
  phases: PlanPhase[] | undefined,
  timeAxis: TimeAxis
): PhaseBand[] {
  const axisStart = axisStartMs(timeAxis);
  const span = axisSpanMs(timeAxis);
  if (axisStart === undefined || span === undefined) return [];

  // The axis's own wall clock: minutes past midnight of its first sample, read
  // at the offset `start_time` carries (the same convention as buildHourTicks).
  const offsetMs = tzOffsetMinutes(timeAxis.start_time) * 60_000;
  const axisLocal = new Date(axisStart + offsetMs);
  const axisMinuteOfDay = axisLocal.getUTCHours() * 60 + axisLocal.getUTCMinutes();
  const MS_PER_MINUTE = 60_000;
  const MINUTES_PER_DAY = 1440;

  const bands: PhaseBand[] = [];
  for (const phase of phases ?? []) {
    const startMin = parseTimeOfDay(phase.time_window?.start ?? '');
    const endMin = parseTimeOfDay(phase.time_window?.end ?? '');
    if (startMin === undefined || endMin === undefined) continue;

    // Minutes from the axis start to the phase start, on the axis's clock. A
    // phase earlier in the day than the axis start belongs to the next day.
    let fromAxis = startMin - axisMinuteOfDay;
    if (fromAxis < 0) fromAxis += MINUTES_PER_DAY;
    const durationMin = endMin > startMin ? endMin - startMin : endMin + MINUTES_PER_DAY - startMin;

    const start = (fromAxis * MS_PER_MINUTE) / span;
    const end = ((fromAxis + durationMin) * MS_PER_MINUTE) / span;
    const clampedStart = Math.max(0, Math.min(1, start));
    const clampedEnd = Math.max(0, Math.min(1, end));
    if (clampedEnd <= clampedStart) continue;

    bands.push({ name: phase.name, start: clampedStart, end: clampedEnd });
  }

  return bands;
}

/** Width of one step on a synthesized axis, matching the optimizer's own. */
const SITE_DAY_STEP_SECONDS = 900;

/** Half-width of the search around UTC midnight: offsets run −12h to +14h. */
const DAY_START_SEARCH_MINUTES = 15 * 60;

/**
 * The first instant, to the minute, on `key`'s calendar day in `timeZone`.
 *
 * Found by searching {@link zonedDayKey} rather than by offset arithmetic, so a
 * zone that changes offset *at* local midnight (America/Asuncion) still
 * resolves: a spring-forward gap starts the day at 01:00, which is when it
 * really begins, and a 24:00→23:00 fold yields the one 00:00 there is.
 */
function zonedDayStartMs(key: string, timeZone: string): number {
  const [year, month, day] = key.split('-').map(Number);
  const utcMidnightMin = Date.UTC(year, month - 1, day) / 60_000;
  let lo = utcMidnightMin - DAY_START_SEARCH_MINUTES;
  let hi = utcMidnightMin + DAY_START_SEARCH_MINUTES;
  // Invariant: `lo` is before the day, `hi` is on or after it. Day keys order as strings.
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (zonedDayKey(new Date(mid * 60_000), timeZone) >= key) hi = mid;
    else lo = mid;
  }
  return hi * 60_000;
}

/** `±HH:MM` for a signed minute offset. */
function formatOffset(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

export function siteDayAxis(now: Date, timeZone: string): TimeAxis {
  const key = zonedDayKey(now, timeZone);
  const start = zonedDayStartMs(key, timeZone);
  const end = zonedDayStartMs(nextDayKey(key), timeZone);
  const offset = zoneOffsetMinutes(new Date(start), timeZone);
  const wall = new Date(start + offset * 60_000).toISOString().slice(0, 19);
  return {
    start_time: `${wall}${formatOffset(offset)}`,
    step_seconds: SITE_DAY_STEP_SECONDS,
    count: Math.round((end - start) / (SITE_DAY_STEP_SECONDS * 1000)),
  };
}
